/**
 * Shared product option resolution for PDP + AddToCartModal.
 *
 * Rules:
 * - Use only admin-configured sizes/colors/custom_variants (never invent full catalogs).
 * - Size-like "EXTRA" / Velikost custom variants are folded into adult/child sizes.
 * - Disabled / empty custom_variants do not appear as required EXTRA.
 */

const ADULT_LETTER_RE = /^(XXS|XS|S|M|L|XL|XXL|XXXL|3XL|4XL|5XL|ONE\s*SIZE|ONESIZE)$/i;
const SIZE_LIKE_LABEL_RE = /^(extra|velikost(?:i)?|size|sizes|věk|vek|age|dětsk.*|detsk.*|dospěl.*|dospel.*)$/i;

export function classifySizeOption(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  if (ADULT_LETTER_RE.test(s)) return "adult";
  // 7-8, 15+, 15-18let, 15-18 let → child age
  const compact = s.replace(/\s+/g, "").replace(/let$/i, "");
  if (/^\d{1,2}[-–]\d{1,2}$/.test(compact) || /^\d{1,2}[+＋]$/.test(compact)) return "child";
  return null;
}

function normalizeSizeLabel(raw, kind) {
  let s = String(raw ?? "").trim();
  if (kind === "child") {
    // "15-18let" / "15-18 let" → "15-18"; "15+let" → "15+"
    s = s.replace(/\s+/g, "");
    s = s.replace(/let$/i, "");
  }
  return s;
}

export function isSizeLikeCustomVariant(cv) {
  if (!cv) return false;
  const label = String(cv.label || "").trim();
  const options = (cv.options || []).map((o) => String(o).trim()).filter(Boolean);
  if (!options.length) return false;
  if (label && SIZE_LIKE_LABEL_RE.test(label)) return true;
  return options.every((o) => classifySizeOption(o) != null);
}

function uniqPush(arr, value) {
  if (!arr.includes(value)) arr.push(value);
}

/**
 * @returns {{
 *   sizeType: 'adult'|'child'|'both'|'legacy'|null,
 *   adultSizes: string[],
 *   childSizes: string[],
 *   legacySizes: string[],
 *   colors: {name:string,hex:string}[],
 *   customVariants: {label:string,options:string[],required:boolean}[],
 *   showSizes: boolean,
 *   showColors: boolean,
 *   showCustom: boolean,
 *   hasAdultChildToggle: boolean,
 *   allSizeLabels: string[],
 * }}
 */
export function resolveProductOptions(product) {
  if (!product) {
    return {
      sizeType: null,
      adultSizes: [],
      childSizes: [],
      legacySizes: [],
      colors: [],
      customVariants: [],
      showSizes: false,
      showColors: false,
      showCustom: false,
      hasAdultChildToggle: false,
      allSizeLabels: [],
    };
  }

  const legacySizes = Array.isArray(product.sizes)
    ? product.sizes.map((s) => String(s).trim()).filter(Boolean)
    : [];

  let adultSizes = Array.isArray(product.sizes_adult)
    ? product.sizes_adult.map((s) => String(s).trim()).filter(Boolean)
    : [];
  let childSizes = Array.isArray(product.sizes_child)
    ? product.sizes_child.map((s) => String(s).trim()).filter(Boolean)
    : [];

  // Legacy `sizes` only when modern adult/child arrays are both empty
  const useLegacy = adultSizes.length === 0 && childSizes.length === 0 && legacySizes.length > 0;

  const rawCustom = Array.isArray(product.custom_variants) ? product.custom_variants : [];
  const customVariants = [];

  for (const cv of rawCustom) {
    const label = String(cv?.label || "").trim();
    const options = (cv?.options || []).map((o) => String(o).trim()).filter(Boolean);
    if (!label || !options.length) continue; // disabled / empty → drop

    if (isSizeLikeCustomVariant({ label, options })) {
      for (const opt of options) {
        const kind = classifySizeOption(opt) || "child";
        const norm = normalizeSizeLabel(opt, kind);
        if (kind === "adult") uniqPush(adultSizes, norm);
        else uniqPush(childSizes, norm);
      }
      continue; // do not keep as EXTRA
    }

    customVariants.push({
      label,
      options,
      required: cv.required !== false,
    });
  }

  let sizeType = product.size_type || null;
  if (!sizeType) {
    if (adultSizes.length && childSizes.length) sizeType = "both";
    else if (childSizes.length) sizeType = "child";
    else if (adultSizes.length) sizeType = "adult";
    else if (useLegacy) sizeType = "legacy";
  }

  // If size_type says both/adult/child but arrays empty after fold, trust arrays (no catalog invent).
  if (sizeType === "both" && !(adultSizes.length && childSizes.length)) {
    if (adultSizes.length && !childSizes.length) sizeType = "adult";
    else if (childSizes.length && !adultSizes.length) sizeType = "child";
  }

  const hasAnySize = adultSizes.length > 0 || childSizes.length > 0 || useLegacy;
  // Show size UI whenever configured sizes exist (don't require product_type === textile).
  const showSizes = hasAnySize;
  const colors = Array.isArray(product.colors) ? product.colors.filter((c) => c && c.name) : [];
  const showColors = colors.length > 0;
  const showCustom = customVariants.length > 0;
  const hasAdultChildToggle = sizeType === "both" && adultSizes.length > 0 && childSizes.length > 0 && !useLegacy;

  const allSizeLabels = useLegacy
    ? [...legacySizes]
    : [...adultSizes, ...childSizes];

  return {
    sizeType: useLegacy ? "legacy" : sizeType,
    adultSizes,
    childSizes,
    legacySizes: useLegacy ? legacySizes : [],
    colors,
    customVariants,
    showSizes,
    showColors,
    showCustom,
    hasAdultChildToggle,
    allSizeLabels,
  };
}

/** Fold size-like custom variants into size arrays for admin save payloads. */
export function foldSizeLikeCustomsIntoSizes({
  sizesAdult = [],
  sizesChild = [],
  customVariants = [],
}) {
  const adult = [...(sizesAdult || [])];
  const child = [...(sizesChild || [])];
  const kept = [];
  for (const cv of customVariants || []) {
    const label = String(cv?.label || "").trim();
    const options = (cv?.options || []).map((o) => String(o).trim()).filter(Boolean);
    if (!label || !options.length) continue;
    if (isSizeLikeCustomVariant({ label, options })) {
      for (const opt of options) {
        const kind = classifySizeOption(opt) || "child";
        const norm = normalizeSizeLabel(opt, kind);
        if (kind === "adult") uniqPush(adult, norm);
        else uniqPush(child, norm);
      }
      continue;
    }
    kept.push({
      label,
      options,
      required: cv.required !== false,
    });
  }
  return { sizesAdult: adult, sizesChild: child, customVariants: kept };
}
